import { ArrowClockwise, Warehouse } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Checkbox, Tag, Tooltip } from 'antd';
import { MuteText } from '../../elements';
import {
  CommonUtility,
  DateFormat,
  DateUtility,
  theme,
  tooltipTexts,
} from '../../utility';
import { OrganisationAvatar } from '../../components/ImageAvatar';
import {
  getPauseReasonLabel,
  getPendingFulfillmentItems,
  ItemInfoPopup,
  NameContainer,
  OrderTime,
  PauseReason,
  PendingFulfillmentTooltip,
  pendingIssueTypes,
} from './list';
import { ColumnHeaderTooltip, OrderRowActionsDropdown } from './OrderTableControls';

function getProvider(order) {
  return order?.items?.[0]?.providerDetails?.[0];
}

function getTotalItems(order) {
  return (order?.items || []).reduce((total, item) => total + item.qty, 0);
}

function renderPausedStatus(order) {
  const pendingItems = getPendingFulfillmentItems(order);
  const nonPickLocationItems = pendingItems.filter(
    item => item?.issue === pendingIssueTypes.nonPickLocation,
  );
  const isAwaitingWaveEnqueue =
    pendingItems.length > 0 &&
    pendingItems.every(item => item?.issue === pendingIssueTypes.awaitingWaveEnqueue);
  const pauseReason = getPauseReasonLabel(order?.wms_fulfillment_pause?.reason);

  let tagColor = 'default';
  let tagMessage = pauseReason;
  let tooltipItems = pendingItems;
  let tooltipMessage = pauseReason;

  if (nonPickLocationItems.length) {
    const itemCount = CommonUtility.numberWithCommas(nonPickLocationItems.length);
    tagColor = 'warning';
    tagMessage = `Paused: ${itemCount} item(s) in non-pick location`;
    tooltipItems = nonPickLocationItems;
    tooltipMessage =
      'This order is paused because these items are in non-pick locations.';
  } else if (isAwaitingWaveEnqueue) {
    tagColor = 'success';
    tagMessage = 'Ready: waiting for wave enqueue';
    tooltipMessage =
      'This order is waiting for wave enqueue. Select it and press resume.';
  }

  return (
    <PauseReason>
      <Tooltip
        title={
          <PendingFulfillmentTooltip message={tooltipMessage} items={tooltipItems} />
        }
      >
        <Tag color={tagColor}>{tagMessage}</Tag>
      </Tooltip>
    </PauseReason>
  );
}

export function PausedOrderColumns({
  ageSortOrder,
  bulkSelectableOrders = [],
  onBulkSelectToggle,
  onBulkSelectHeaderToggle,
  onRecalculatePaused,
  recalculatingOrderId = null,
  recalculatingPaused = false,
  selectedById = {},
}) {
  const selectedCount = Object.keys(selectedById).length;
  const headerSelectedCount = bulkSelectableOrders.filter(
    order => selectedById[order._id],
  ).length;
  const headerChecked =
    bulkSelectableOrders.length > 0 &&
    headerSelectedCount === bulkSelectableOrders.length;
  const headerIndeterminate = selectedCount > 0 && !headerChecked;

  return [
    {
      title: (
        <div className="d-flex align-items-center">
          <Checkbox
            checked={headerChecked}
            disabled={recalculatingPaused || bulkSelectableOrders.length === 0}
            indeterminate={headerIndeterminate}
            onChange={event => onBulkSelectHeaderToggle?.(event.target.checked)}
            className="mr-2"
          />
          <ColumnHeaderTooltip label="Order ID" helpText={tooltipTexts.orderId} />
        </div>
      ),
      key: 'order_id',
      align: 'left',
      width: '20%',
      render: (_, order) => {
        const provider = getProvider(order);
        const bulkSelected = !!selectedById[order._id];
        const isRowRecalculating =
          recalculatingPaused || recalculatingOrderId === order._id;

        return (
          <div className="d-flex align-items-start">
            <Checkbox
              checked={bulkSelected}
              disabled={isRowRecalculating}
              onChange={event => onBulkSelectToggle?.(order, event.target.checked)}
              className="mr-2 mt-1"
            />
            <div>
              {provider ? (
                <Link
                  to={`/app/fulfill/orders/details/${order._id}/?providerId=${provider?._id}`}
                >
                  {order.marketplaceOrderDisplayId}
                </Link>
              ) : (
                order.marketplaceOrderDisplayId
              )}
              {renderPausedStatus(order)}
            </div>
          </div>
        );
      },
    },
    {
      title: <ColumnHeaderTooltip label="Age" helpText={tooltipTexts.orderAge} />,
      key: 'timeAcknowledged',
      align: 'left',
      width: '14%',
      sorter: true,
      sortOrder: ageSortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (_, order) =>
        order.timeAcknowledged ? (
          <>
            <OrderTime time={order.timeAcknowledged} isEnabled />
            <div>
              <MuteText>
                {DateUtility.formatDate(order.timeAcknowledged, DateFormat.barChartMonth)}
              </MuteText>
            </div>
          </>
        ) : null,
    },
    {
      title: 'Warehouse',
      key: 'warehouse',
      align: 'left',
      width: '16%',
      render: (_, order) => {
        const provider = getProvider(order);

        return provider?.warehouseName ? (
          <div className="d-flex justify-content-start align-items-center">
            <Warehouse size={20} className="mr-2" color={theme.colors.grey} />
            <NameContainer>{provider?.warehouseName}</NameContainer>
          </div>
        ) : null;
      },
    },
    {
      title: (
        <ColumnHeaderTooltip label="Organization" helpText={tooltipTexts.organization} />
      ),
      key: 'organization',
      align: 'left',
      width: '18%',
      render: (_, order) => (
        <div className="d-flex justify-content-start align-items-center">
          <OrganisationAvatar image={order.logo} name={order.organizationName} />
          <NameContainer>&nbsp;{order.organizationName}</NameContainer>
        </div>
      ),
    },
    {
      title: <ColumnHeaderTooltip label="Customer" />,
      key: 'customer',
      align: 'left',
      width: '11%',
      render: (_, order) => (
        <div className="d-flex justify-content-start align-items-center">
          &nbsp;{order.shippingAddress?.name || <MuteText>N/A</MuteText>}
        </div>
      ),
    },
    {
      title: <ColumnHeaderTooltip label="Items" helpText={tooltipTexts.totalItems} />,
      key: 'total_items',
      align: 'center',
      width: '20%',
      render: (_, order) => {
        const totalItems = getTotalItems(order);
        return (
          <div className="d-flex justify-content-center align-items-center flex-column">
            <ItemInfoPopup
              items={order.items}
              position="top left"
              total_items={CommonUtility.numberWithCommas(totalItems)}
            />
          </div>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      align: 'center',
      fixed: 'right',
      width: 72,
      render: (_, order) => {
        const isRowRecalculating =
          recalculatingPaused || recalculatingOrderId === order._id;

        return (
          <OrderRowActionsDropdown
            items={[
              {
                key: 'recalculate',
                label: isRowRecalculating
                  ? 'Recalculating...'
                  : 'Recalculate Fulfillment',
                icon: <ArrowClockwise />,
                disabled: isRowRecalculating,
              },
            ]}
            onAction={() => onRecalculatePaused?.(order._id)}
          />
        );
      },
    },
  ];
}
